import {
  type ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useShow, useCustomMutation, useNavigation } from '@refinedev/core';
import { Show } from '@refinedev/antd';
import {
  Typography,
  Descriptions,
  Tag,
  Card,
  Space,
  Button,
  Tooltip,
  App,
  Collapse,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  Image,
} from 'antd';
import {
  EyeOutlined,
  FormOutlined,
  PictureOutlined,
  FolderOpenOutlined,
  PlayCircleOutlined,
  DownloadOutlined,
  EditOutlined,
  ReloadOutlined,
} from '@ant-design/icons';

import {
  calculatePrice,
  formatPrice,
  SHOT_TYPES,
  COVER_REGEN_LIMIT,
  BOOK_REGEN_LIMIT,
  PAGE_REGEN_LIMIT,
  REGEN_INSTRUCTIONS_MAX_LENGTH,
  type PageCountBookType,
  type PageOverrides,
  type RegeneratePageRequest,
  type RegenerateResponse,
} from '@woolaby/types';

import { axiosInstance, type HttpError } from '@/providers/axiosInstance';
import type {
  AdminExample,
  AdminOrderDetail,
  AdminOrderPreview,
  OrderStatus,
} from '@/types';
import { openWithAdminToken } from '@/utils/adminPreview';
import { hasStorageOutput } from '@/utils/storage';

import { ShareLinkButton } from './ShareLinkButton';

const { Title, Text } = Typography;

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const STATUS_COLORS: Record<OrderStatus, string> = {
  INIT: 'default',
  STARTED: 'gold',
  PAYMENT_PENDING: 'gold',
  PAYMENT_COMPLETED: 'blue',
  PAYMENT_FAILED: 'red',
  GENERATING: 'processing',
  GENERATING_PLAN: 'processing',
  GENERATING_CHARACTER_SHEET: 'processing',
  GENERATING_STORYBOARD: 'processing',
  GENERATING_FINAL: 'processing',
  PLAN_REVIEW: 'orange',
  CHARACTER_SHEET_REVIEW: 'orange',
  STORYBOARD_REVIEW: 'orange',
  GENERATION_COMPLETE: 'green',
  GENERATION_FAILED: 'red',
  PREVIEW_READY: 'green',
  DRAFT_READY: 'lime',
  CANCELLED: 'default',
  PRINT_JOB_SUBMITTED: 'cyan',
  SHIPPED: 'green',
};

const PREVIEW_ELIGIBLE_STATUSES: OrderStatus[] = [
  'PREVIEW_READY',
  'GENERATION_COMPLETE',
  'PRINT_JOB_SUBMITTED',
  'SHIPPED',
];

const CONFIRMATION_ELIGIBLE_STATUSES: OrderStatus[] = [
  'STARTED',
  'PAYMENT_PENDING',
  'PAYMENT_COMPLETED',
  'GENERATING',
  'GENERATING_PLAN',
  'GENERATING_CHARACTER_SHEET',
  'GENERATING_STORYBOARD',
  'GENERATING_FINAL',
  'PLAN_REVIEW',
  'CHARACTER_SHEET_REVIEW',
  'STORYBOARD_REVIEW',
  'GENERATION_COMPLETE',
  'GENERATION_FAILED',
  'PREVIEW_READY',
  'PRINT_JOB_SUBMITTED',
  'SHIPPED',
];

const GENERATING_ELIGIBLE_STATUSES: OrderStatus[] = [
  'GENERATING',
  'GENERATING_PLAN',
  'GENERATING_CHARACTER_SHEET',
  'GENERATING_STORYBOARD',
  'GENERATING_FINAL',
  'PLAN_REVIEW',
  'CHARACTER_SHEET_REVIEW',
  'STORYBOARD_REVIEW',
  'GENERATION_FAILED',
];

/** Statuses where an order can be turned into an example */
const EXAMPLE_ELIGIBLE_STATUSES: OrderStatus[] = [
  'PREVIEW_READY',
  'GENERATION_COMPLETE',
  'PRINT_JOB_SUBMITTED',
  'SHIPPED',
];

/**
 * Statuses where the admin per-page regeneration action is offered — ported
 * verbatim from `origin/develop`'s admin `OrderShow.tsx` (`Regenerate Page`
 * button). Deliberately just `PREVIEW_READY`, not the wider
 * `EXAMPLE_ELIGIBLE_STATUSES`/`PREVIEW_ELIGIBLE_STATUSES` sets: the server's
 * own claim (`book/revisions.ts`'s `claimRegenerationOperation`) requires
 * `orders.status === 'PREVIEW_READY'` for a fresh claim, so gating the
 * button any wider would only surface a 409 the admin can already avoid by
 * reading the status column.
 */
const PAGE_REGEN_ELIGIBLE_STATUSES: OrderStatus[] = ['PREVIEW_READY'];

/**
 * Statuses where the admin "Regenerate" restart action is offered. Mirrors `book/runs.ts`'s exported
 * `START_OVER_STATUSES` (`['GENERATION_FAILED', ...IN_FLIGHT_STATUSES]`)
 * exactly — the server route (`POST /api/admin/orders/:id/regenerate`)
 * accepts precisely this set (routing a non-content-filter
 * `GENERATION_FAILED` order through the cheaper `tryAgain` internally, and
 * everything else here through `startOver`), so gating the button any
 * wider would only surface a 409 the admin can already avoid by reading
 * the status column. Not the historical `REGENERATE_ELIGIBLE_STATUSES`
 * superset — that also listed the legacy LangGraph-only
 * `GENERATING`/`GENERATING_STORYBOARD`/`STORYBOARD_REVIEW` statuses, which
 * `book/runs.ts` has no run-restart path for at all: no `book/` run was
 * ever created for those rows, so neither `tryAgain` nor `startOver` could
 * safely act on one. 🚨 If `book/runs.ts`'s `START_OVER_STATUSES` ever
 * changes, this list drifts silently — there is no shared constant.
 */
const RESTART_ELIGIBLE_STATUSES: OrderStatus[] = [
  'GENERATION_FAILED',
  'GENERATING_PLAN',
  'PLAN_REVIEW',
  'GENERATING_CHARACTER_SHEET',
  'CHARACTER_SHEET_REVIEW',
  'GENERATING_FINAL',
];

const SUPABASE_STORAGE_URL: string | undefined = import.meta.env
  .VITE_SUPABASE_STORAGE_URL;

/** PDF artifacts downloadable from the preview endpoint's signed URLs */
type PdfScope = 'book' | 'cover' | 'digital';

const PDF_SCOPE_LABELS: Record<PdfScope, string> = {
  book: 'book.pdf',
  cover: 'cover.pdf',
  digital: 'digital.pdf',
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatCents(cents: number | null): string {
  if (cents === null || cents === undefined) return '-';
  return `$${(cents / 100).toFixed(2)}`;
}

function formatDurationMs(ms: number | null): string {
  if (ms === null || ms === undefined) return '-';
  const totalSeconds = Math.round(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (minutes === 0) return `${String(seconds)}s`;
  return `${String(minutes)}m ${String(seconds)}s`;
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleString();
}

/** Render a JSONB object as a pretty-printed code block */
function JsonSection({
  title,
  data,
}: {
  title: string;
  data: Record<string, unknown> | null;
}): ReactElement | null {
  if (!data) return null;

  return (
    <Card size="small" title={title} style={{ marginBottom: 16 }}>
      <pre
        style={{
          maxHeight: 400,
          overflow: 'auto',
          fontSize: 12,
          background: '#f5f5f5',
          padding: 12,
          borderRadius: 6,
          margin: 0,
        }}
      >
        {JSON.stringify(data, null, 2)}
      </pre>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Admin regeneration action — the historical `RegeneratePageModal` against
// `POST /api/admin/orders/:id/regenerate-page`, which runs through the SAME
// transactional service (`book/regeneration.ts`'s `claimAndDispatchPageRedraw`)
// the customer route uses, but with `decrementCount: false` (the admin
// exception — the customer's own `pageRegenerationCount` is never spent)
// and honors `pageOverrides` (rejected on the customer route). No duplicate
// UI: this is the ONLY page-regeneration surface in the admin panel.
// ---------------------------------------------------------------------------

interface AdminRegeneratePageModalProps {
  open: boolean;
  onClose: () => void;
  orderId: string;
  onSuccess: () => void;
}

interface AdvancedFormValues {
  imagePrompt?: string;
  text?: string;
  shotType?: (typeof SHOT_TYPES)[number];
  setting?: string;
}

interface AdminPreviewPagesResponse {
  pageUrls: string[];
}

/**
 * Admin per-page regeneration modal. Ported from
 * `origin/develop:apps/admin/src/pages/orders/RegeneratePageModal.tsx`
 * (#395), adapted to the restored `book/` backend:
 * - The response now carries `{ status: 'started', runId }` —
 *   the operation is dispatched in the BACKGROUND and this call returns as
 *   soon as the claim lands, so the success message names the run rather
 *   than implying completion.
 * - The historical component fetched page URLs via
 *   `GET /api/admin/orders/:id/preview` — unchanged here; that endpoint's
 *   shape survived the LangGraph deletion untouched (`AdminOrderPreview`).
 * - The historical component read the original page JSON from a LangGraph
 *   checkpoint that no longer exists, so — exactly like the historical
 *   one — the Advanced fields start blank; only non-empty fields are sent
 *   as overrides, leaving the candidate revision's own copy of the page
 *   untouched for anything the admin does not type.
 */
function AdminRegeneratePageModal({
  open,
  onClose,
  orderId,
  onSuccess,
}: AdminRegeneratePageModalProps): ReactElement {
  const { message } = App.useApp();
  const [form] = Form.useForm<AdvancedFormValues>();

  const [pageImageUrls, setPageImageUrls] = useState<string[]>([]);
  const [loadingPages, setLoadingPages] = useState(false);
  const [selectedPageIndex, setSelectedPageIndex] = useState(0);
  const [additionalPrompt, setAdditionalPrompt] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Fetch page URLs when the modal opens.
  useEffect(() => {
    if (!open) return;
    setLoadingPages(true);
    axiosInstance
      .get<AdminPreviewPagesResponse>(`/api/admin/orders/${orderId}/preview`)
      .then((res) => {
        setPageImageUrls(res.data.pageUrls ?? []);
      })
      .catch((error: HttpError) => {
        message.error(
          `Failed to load page previews: ${error?.message ?? 'Unknown error'}`
        );
      })
      .finally(() => {
        setLoadingPages(false);
      });
  }, [open, orderId, message]);

  // Reset state on close.
  useEffect(() => {
    if (!open) {
      setSelectedPageIndex(0);
      setAdditionalPrompt('');
      setPageImageUrls([]);
      form.resetFields();
    }
  }, [open, form]);

  // Reset Advanced overrides when the selected page changes — the admin
  // types only the fields they want to override, so keep the form blank by
  // default.
  useEffect(() => {
    form.resetFields();
  }, [selectedPageIndex, form]);

  const pageOptions = useMemo(
    () =>
      pageImageUrls.map((_, i) => ({
        value: i,
        label: `Page ${String(i + 1)}`,
      })),
    [pageImageUrls]
  );

  const shotTypeOptions = useMemo(
    () => SHOT_TYPES.map((s) => ({ value: s, label: s })),
    []
  );

  const safeIndex = Math.min(
    Math.max(selectedPageIndex, 0),
    Math.max(pageImageUrls.length - 1, 0)
  );
  const currentImage = pageImageUrls[safeIndex];

  const handleSubmit = useCallback(async (): Promise<void> => {
    const advanced = form.getFieldsValue();

    // Strip empty / whitespace-only string overrides.
    const overrides: NonNullable<PageOverrides> = {};
    if (advanced.imagePrompt?.trim()) {
      overrides.imagePrompt = advanced.imagePrompt.trim();
    }
    if (advanced.text?.trim()) {
      overrides.text = advanced.text.trim();
    }
    if (advanced.shotType) {
      overrides.shotType = advanced.shotType;
    }
    if (advanced.setting?.trim()) {
      overrides.setting = advanced.setting.trim();
    }

    const hasOverrides = Object.keys(overrides).length > 0;

    const body: RegeneratePageRequest = {
      pageIndex: safeIndex,
      ...(additionalPrompt.trim() && {
        additionalPrompt: additionalPrompt.trim(),
      }),
      ...(hasOverrides && { pageOverrides: overrides }),
    };

    setSubmitting(true);
    try {
      const response = await axiosInstance.post<RegenerateResponse>(
        `/api/admin/orders/${orderId}/regenerate-page`,
        body
      );
      message.success(
        `Page regeneration started (run ${response.data.runId}). The customer's own page regeneration count is unaffected.`
      );
      onSuccess();
      onClose();
    } catch (error) {
      const err = error as HttpError;
      message.error(
        `Failed to regenerate page: ${err?.message ?? 'Unknown error'}`
      );
    } finally {
      setSubmitting(false);
    }
  }, [form, safeIndex, additionalPrompt, orderId, message, onSuccess, onClose]);

  const hasPages = pageImageUrls.length > 0;

  return (
    <Modal
      title="Regenerate Page"
      open={open}
      onCancel={onClose}
      width={640}
      footer={
        <Space>
          <Button onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            type="primary"
            loading={submitting}
            disabled={!hasPages || loadingPages}
            onClick={() => {
              void handleSubmit();
            }}
          >
            Regenerate
          </Button>
        </Space>
      }
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <div>
          <Text strong>Page</Text>
          <Select
            style={{ width: '100%', marginTop: 8 }}
            value={hasPages ? safeIndex : undefined}
            options={pageOptions}
            onChange={(value: number) => setSelectedPageIndex(value)}
            disabled={loadingPages || !hasPages}
            placeholder={loadingPages ? 'Loading pages…' : 'No pages available'}
          />
        </div>

        {currentImage && (
          <div style={{ textAlign: 'center' }}>
            <Image
              src={currentImage}
              alt={`Page ${String(safeIndex + 1)} preview`}
              style={{ maxWidth: 240, maxHeight: 240 }}
            />
          </div>
        )}

        <div>
          <Text strong>Additional prompt</Text>
          <Input.TextArea
            rows={3}
            maxLength={REGEN_INSTRUCTIONS_MAX_LENGTH}
            value={additionalPrompt}
            onChange={(e) => setAdditionalPrompt(e.target.value)}
            placeholder="Optional steering instructions for the regeneration."
            disabled={submitting}
            style={{ marginTop: 8 }}
          />
        </div>

        <Collapse
          items={[
            {
              key: 'advanced',
              label: 'Advanced (override page JSON)',
              children: (
                <>
                  <Text
                    type="secondary"
                    style={{ display: 'block', marginBottom: 12 }}
                  >
                    Leave fields empty to keep the candidate revision's existing
                    page values. Only non-empty fields are sent as overrides.
                  </Text>
                  <Form form={form} layout="vertical" disabled={submitting}>
                    <Form.Item label="Image prompt" name="imagePrompt">
                      <Input.TextArea rows={3} />
                    </Form.Item>
                    <Form.Item label="Text" name="text">
                      <Input.TextArea rows={3} />
                    </Form.Item>
                    <Form.Item label="Shot type" name="shotType">
                      <Select
                        options={shotTypeOptions}
                        allowClear
                        placeholder="No override"
                      />
                    </Form.Item>
                    <Form.Item label="Setting" name="setting">
                      <Input />
                    </Form.Item>
                  </Form>
                </>
              ),
            },
          ]}
        />
      </Space>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Admin bulk regeneration-count override. Restored from `origin/develop:apps/admin/src/pages/orders/EditRegenerationCountsForm.tsx`
// (#395) against the RESTORED `PATCH /api/admin/orders/:id/regeneration-counts`
// route (`routes/admin/orders.ts`) — unchanged in shape; the
// server's own audit notification now fires through `book/steps/notify.ts`'s
// `notifyTeam` (the current Telegram-backed team channel) instead of the
// deleted `telegramNotifier.ts`, which is a server-side concern this
// component does not need to know about.
// ---------------------------------------------------------------------------

interface EditRegenerationCountsFormProps {
  orderId: string;
  coverRegenerationCount: number;
  bookRegenerationCount: number;
  pageRegenerationCount: number;
  onSuccess: () => void;
}

interface RegenerationCountsPatchBody {
  coverRegenerationCount?: number;
  bookRegenerationCount?: number;
  pageRegenerationCount?: number;
}

function helperText(current: number, limit: number): string {
  const remaining = Math.max(0, limit - current);
  return `${String(current)} / ${String(limit)} (${String(remaining)} remaining)`;
}

function EditRegenerationCountsForm({
  orderId,
  coverRegenerationCount,
  bookRegenerationCount,
  pageRegenerationCount,
  onSuccess,
}: EditRegenerationCountsFormProps): ReactElement {
  const { message, modal } = App.useApp();

  const [coverValue, setCoverValue] = useState<number>(coverRegenerationCount);
  const [bookValue, setBookValue] = useState<number>(bookRegenerationCount);
  const [pageValue, setPageValue] = useState<number>(pageRegenerationCount);
  const [submitting, setSubmitting] = useState(false);

  const changedFields = useMemo((): RegenerationCountsPatchBody => {
    const body: RegenerationCountsPatchBody = {};
    if (coverValue !== coverRegenerationCount) {
      body.coverRegenerationCount = coverValue;
    }
    if (bookValue !== bookRegenerationCount) {
      body.bookRegenerationCount = bookValue;
    }
    if (pageValue !== pageRegenerationCount) {
      body.pageRegenerationCount = pageValue;
    }
    return body;
  }, [
    coverValue,
    bookValue,
    pageValue,
    coverRegenerationCount,
    bookRegenerationCount,
    pageRegenerationCount,
  ]);

  const hasChanges = Object.keys(changedFields).length > 0;

  const summaryText = useMemo((): string => {
    const parts: string[] = [];
    if (changedFields.coverRegenerationCount !== undefined) {
      parts.push(`cover=${String(changedFields.coverRegenerationCount)}`);
    }
    if (changedFields.bookRegenerationCount !== undefined) {
      parts.push(`book=${String(changedFields.bookRegenerationCount)}`);
    }
    if (changedFields.pageRegenerationCount !== undefined) {
      parts.push(`page=${String(changedFields.pageRegenerationCount)}`);
    }
    return `Set ${parts.join(', ')}?`;
  }, [changedFields]);

  const handleSave = useCallback((): void => {
    if (!hasChanges) return;
    modal.confirm({
      title: 'Update regeneration counts?',
      content: summaryText,
      okText: 'Save',
      cancelText: 'Cancel',
      onOk: async (): Promise<void> => {
        setSubmitting(true);
        try {
          await axiosInstance.patch(
            `/api/admin/orders/${orderId}/regeneration-counts`,
            changedFields
          );
          message.success('Regeneration counts updated');
          onSuccess();
        } catch (error) {
          const err = error as HttpError;
          message.error(
            `Failed to update counts: ${err?.message ?? 'Unknown error'}`
          );
        } finally {
          setSubmitting(false);
        }
      },
    });
  }, [
    hasChanges,
    modal,
    summaryText,
    orderId,
    changedFields,
    message,
    onSuccess,
  ]);

  return (
    <Card
      title="Regeneration Attempts"
      size="small"
      style={{ marginTop: 16, marginBottom: 16 }}
    >
      <Form layout="vertical" disabled={submitting}>
        <Form.Item
          label="Cover"
          help={helperText(coverRegenerationCount, COVER_REGEN_LIMIT)}
        >
          <InputNumber
            min={0}
            value={coverValue}
            onChange={(v) => {
              if (typeof v === 'number') setCoverValue(v);
            }}
          />
        </Form.Item>
        <Form.Item
          label="Book"
          help={helperText(bookRegenerationCount, BOOK_REGEN_LIMIT)}
        >
          <InputNumber
            min={0}
            value={bookValue}
            onChange={(v) => {
              if (typeof v === 'number') setBookValue(v);
            }}
          />
        </Form.Item>
        <Form.Item
          label="Page"
          help={helperText(pageRegenerationCount, PAGE_REGEN_LIMIT)}
        >
          <InputNumber
            min={0}
            value={pageValue}
            onChange={(v) => {
              if (typeof v === 'number') setPageValue(v);
            }}
          />
        </Form.Item>
        <Button
          type="primary"
          disabled={!hasChanges || submitting}
          loading={submitting}
          onClick={handleSave}
        >
          Save
        </Button>
      </Form>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function OrderShow(): ReactElement {
  const { query } = useShow<AdminOrderDetail>();
  const record = query.data?.data;
  const isLoading = query.isLoading;

  const { message } = App.useApp();
  const { list } = useNavigation();
  const { mutate: makeExample, isLoading: isMakingExample } =
    useCustomMutation<AdminExample>();
  const [madeExample, setMadeExample] = useState(false);
  const [downloadingPdfScope, setDownloadingPdfScope] =
    useState<PdfScope | null>(null);
  const [pageRegenModalOpen, setPageRegenModalOpen] = useState(false);
  const [restarting, setRestarting] = useState(false);

  const handleOpenGenerating = useCallback(() => {
    if (!record) return;
    openWithAdminToken(`/book/generating/${record.id}`);
  }, [record]);

  /**
   * Restart a stuck or failed order.
   * `POST /api/admin/orders/:id/regenerate` delegates to `book/runs.ts`'s
   * `tryAgain` (preserves the approved plan) or `startOver` (a genuinely
   * fresh run) — never a same-run `restart()`, never a raw status write.
   * Returns `{ runId }`; the row's own status transition (visible after
   * `query.refetch()`) is the real confirmation, not this response.
   */
  const handleRegenerateRestart = useCallback(() => {
    if (!record) return;
    setRestarting(true);
    axiosInstance
      .post<{ runId: string }>(`/api/admin/orders/${record.id}/regenerate`)
      .then((response) => {
        message.success(`Regeneration restarted (run ${response.data.runId}).`);
        void query.refetch();
      })
      .catch((error: HttpError) => {
        message.error(
          `Failed to restart: ${error?.message ?? 'Unknown error'}`
        );
      })
      .finally(() => {
        setRestarting(false);
      });
  }, [record, message, query]);

  const handleDownloadPdf = useCallback(
    (scope: PdfScope) => {
      if (!record) return;

      // Open the tab synchronously in the click handler so popup blockers
      // allow it, then navigate once the signed URL resolves (same pattern
      // as `openWithAdminToken`, inlined here since this hits a different
      // endpoint and needs per-scope null handling). We must NOT pass
      // `noopener`/`noreferrer` to `window.open` here: per the HTML spec
      // those features make it return `null`, which would strand the tab
      // on about:blank forever. Instead we keep the handle and sever the
      // opener link manually before navigating to the cross-origin signed URL.
      const win = window.open('', '_blank');
      setDownloadingPdfScope(scope);

      axiosInstance
        .get<AdminOrderPreview>(`/api/admin/orders/${record.id}/preview`)
        .then((response) => {
          const { bookPdfUrl, coverPdfUrl, digitalPdfUrl } = response.data;
          const url: string | null =
            scope === 'book'
              ? bookPdfUrl
              : scope === 'cover'
                ? coverPdfUrl
                : digitalPdfUrl;

          if (!url) {
            win?.close();
            if (scope === 'digital') {
              message.info('digital.pdf is not available for this order');
            } else {
              message.info(
                `${PDF_SCOPE_LABELS[scope]} does not exist for digital-only orders`
              );
            }
            return;
          }

          if (win) {
            win.opener = null;
            win.location.href = url;
          }
        })
        .catch((err: HttpError) => {
          win?.close();
          if (err.statusCode === 404) {
            message.warning('No generated output for this order yet');
          } else {
            message.error(err.message);
          }
        })
        .finally(() => {
          setDownloadingPdfScope(null);
        });
    },
    [record, message]
  );

  const handleMakeExample = useCallback(() => {
    if (!record) return;
    makeExample(
      {
        url: `/api/admin/orders/${record.id}/make-example`,
        method: 'post',
        values: {},
      },
      {
        onSuccess: () => {
          setMadeExample(true);
          message.success(
            <span>
              Example created successfully.{' '}
              <a onClick={() => list('examples')}>View examples</a>
            </span>
          );
        },
        onError: (error) => {
          message.error(
            `Failed to create example: ${error?.message ?? 'Unknown error'}`
          );
        },
      }
    );
  }, [record, makeExample, message, list]);

  return (
    <Show isLoading={isLoading}>
      {record && (
        <>
          {/* Quick action buttons */}
          <Space wrap style={{ marginBottom: 24 }}>
            {PREVIEW_ELIGIBLE_STATUSES.includes(record.status) && (
              <ShareLinkButton
                path={`/book/preview/${record.id}`}
                type="primary"
                icon={<EyeOutlined />}
                label="Open Preview"
              />
            )}
            {CONFIRMATION_ELIGIBLE_STATUSES.includes(record.status) && (
              <ShareLinkButton
                path={`/book/confirm/${record.id}`}
                icon={<FormOutlined />}
                label="Open Confirmation"
              />
            )}
            {GENERATING_ELIGIBLE_STATUSES.includes(record.status) && (
              <Button
                icon={<PlayCircleOutlined />}
                onClick={handleOpenGenerating}
              >
                Open Generating
              </Button>
            )}
            {hasStorageOutput(record) && SUPABASE_STORAGE_URL && (
              <Tooltip title="Open Supabase Storage folder">
                <Button
                  icon={<FolderOpenOutlined />}
                  href={`${SUPABASE_STORAGE_URL}?prefix=${record.id}/`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Storage
                </Button>
              </Tooltip>
            )}
            {PREVIEW_ELIGIBLE_STATUSES.includes(record.status) && (
              <>
                <Button
                  icon={<DownloadOutlined />}
                  onClick={() => handleDownloadPdf('book')}
                  loading={downloadingPdfScope === 'book'}
                >
                  Download book.pdf
                </Button>
                <Button
                  icon={<DownloadOutlined />}
                  onClick={() => handleDownloadPdf('cover')}
                  loading={downloadingPdfScope === 'cover'}
                >
                  Download cover.pdf
                </Button>
                <Button
                  icon={<DownloadOutlined />}
                  onClick={() => handleDownloadPdf('digital')}
                  loading={downloadingPdfScope === 'digital'}
                >
                  Download digital.pdf
                </Button>
              </>
            )}
            {EXAMPLE_ELIGIBLE_STATUSES.includes(record.status) && (
              <Tooltip title="Create a showcase example from this order">
                <Button
                  icon={<PictureOutlined />}
                  onClick={handleMakeExample}
                  loading={isMakingExample}
                  disabled={madeExample}
                >
                  {madeExample ? 'Example Created' : 'Make Example'}
                </Button>
              </Tooltip>
            )}
            {PAGE_REGEN_ELIGIBLE_STATUSES.includes(record.status) && (
              <Tooltip title="Regenerate a single page — does not consume the customer's own page regeneration count, and accepts advanced overrides the customer route refuses">
                <Button
                  icon={<EditOutlined />}
                  onClick={() => setPageRegenModalOpen(true)}
                >
                  Regenerate Page
                </Button>
              </Tooltip>
            )}
            {RESTART_ELIGIBLE_STATUSES.includes(record.status) && (
              <Tooltip title="Restart a stuck or failed order — preserves the approved plan when safely possible (GENERATION_FAILED, not a content-filter block); starts a genuinely fresh run otherwise">
                <Button
                  icon={<ReloadOutlined />}
                  onClick={handleRegenerateRestart}
                  loading={restarting}
                  danger
                >
                  Regenerate
                </Button>
              </Tooltip>
            )}
          </Space>

          {/* Core order information */}
          <Title level={5}>Order Information</Title>
          <Descriptions bordered column={{ xs: 1, sm: 2, lg: 4 }} size="small">
            <Descriptions.Item label="Order ID">
              <Text copyable>{record.id}</Text>
            </Descriptions.Item>
            <Descriptions.Item label="Status">
              <Tag color={STATUS_COLORS[record.status] ?? 'default'}>
                {record.status}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Book Title">
              {record.bookTitle ?? '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Plot Summary">
              <div style={{ maxWidth: '25vw' }}>
                <Text ellipsis={{ tooltip: record.plotSummary ?? '-' }}>
                  {record.plotSummary ?? '-'}
                </Text>
              </div>
            </Descriptions.Item>
            <Descriptions.Item label="Email">
              {record.email ?? '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Phone">
              {record.phone ?? '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Client">
              {record.client}
            </Descriptions.Item>
            <Descriptions.Item label="App Environment">
              {record.appEnv ?? '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Page Count">
              {record.pageCount ?? '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Order Type">
              {record.paidProductType ?? '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Created At">
              {formatDate(record.createdAt)}
            </Descriptions.Item>
            <Descriptions.Item label="Updated At">
              {formatDate(record.updatedAt)}
            </Descriptions.Item>
          </Descriptions>

          {/* Payment information */}
          <Title level={5} style={{ marginTop: 24 }}>
            Payment
          </Title>
          <Descriptions bordered column={{ xs: 1, sm: 2, lg: 4 }} size="small">
            <Descriptions.Item label="Payment Status">
              {record.paymentStatus ? (
                <Tag
                  color={record.paymentStatus === 'paid' ? 'green' : 'orange'}
                >
                  {record.paymentStatus}
                </Tag>
              ) : (
                '-'
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Amount Total">
              {formatCents(record.amountTotal)}
            </Descriptions.Item>
            <Descriptions.Item label="Configured Price">
              {record.paidProductType && record.pageCount !== null
                ? formatPrice(
                    calculatePrice(
                      record.paidProductType as PageCountBookType,
                      record.pageCount
                    )
                  )
                : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Stripe Session ID">
              {record.stripeSessionId ? (
                <Text copyable style={{ fontSize: 12 }}>
                  {record.stripeSessionId}
                </Text>
              ) : (
                '-'
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Stripe Payment Intent">
              {record.stripePaymentIntentId ? (
                <Text copyable style={{ fontSize: 12 }}>
                  {record.stripePaymentIntentId}
                </Text>
              ) : (
                '-'
              )}
            </Descriptions.Item>
          </Descriptions>

          {/* Cost breakdown */}
          <Title level={5} style={{ marginTop: 24 }}>
            Costs
          </Title>
          <Descriptions bordered column={{ xs: 1, sm: 2, lg: 4 }} size="small">
            <Descriptions.Item label="AI Cost">
              {formatCents(record.aiCostCents)}
            </Descriptions.Item>
            <Descriptions.Item label="AI Duration">
              {formatDurationMs(record.generationDurationMs)}
            </Descriptions.Item>
            <Descriptions.Item label="Lulu Print Cost">
              {formatCents(record.luluPrintCostCents)}
            </Descriptions.Item>
            <Descriptions.Item label="Lulu Ship Cost">
              {formatCents(record.luluShipCostCents)}
            </Descriptions.Item>
          </Descriptions>

          {/* Generation details */}
          <Title level={5} style={{ marginTop: 24 }}>
            Generation
          </Title>
          <Descriptions bordered column={{ xs: 1, sm: 2, lg: 4 }} size="small">
            <Descriptions.Item label="Thread ID">
              {record.threadId ? (
                <Text copyable style={{ fontSize: 12 }}>
                  {record.threadId}
                </Text>
              ) : (
                '-'
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Workflow Run ID">
              {record.workflowRunId ? (
                <Text copyable style={{ fontSize: 12 }}>
                  {record.workflowRunId}
                </Text>
              ) : (
                '-'
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Generated Path">
              {record.generatedPath ? (
                SUPABASE_STORAGE_URL ? (
                  <a
                    href={`${SUPABASE_STORAGE_URL}?prefix=${record.id}/`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {record.generatedPath}
                  </a>
                ) : (
                  record.generatedPath
                )
              ) : (
                '-'
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Session ID">
              {record.sessionId ? (
                <Text copyable style={{ fontSize: 12 }}>
                  {record.sessionId}
                </Text>
              ) : (
                '-'
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Page Text Font Size">
              {record.pageTextFontSize !== null
                ? `${record.pageTextFontSize}px`
                : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Retry Attempts">
              {/*
                Phase 5 — derived from LangGraph `humanPlanApprovalCount`.
                Counts user-approved plans that were rejected / resumed.
              */}
              {record.retryAttempts}
            </Descriptions.Item>
            <Descriptions.Item label="Failure Reason" span={2}>
              {record.failureReason ? (
                <Text type="danger">{record.failureReason}</Text>
              ) : (
                '-'
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Failure Message" span={2}>
              {/*
                Raw dev-jargon failureMessage is shown verbatim — the leak is
                intentional (helps customer support triage). See memory note
                `feedback_failure_message_leak_intentional.md`.
              */}
              {record.failureMessage ? (
                <Text
                  type="danger"
                  style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                >
                  {record.failureMessage}
                </Text>
              ) : (
                '-'
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Failure Category" span={2}>
              {/*
                Issue #677 — recovery class from the AgentError taxonomy
                (e.g. PERMANENT, TRANSIENT, SAFETY). Null for legacy / infra
                failures that predate the taxonomy.
              */}
              {record.failureCategory ? (
                <Tag color="red">{record.failureCategory}</Tag>
              ) : (
                '-'
              )}
            </Descriptions.Item>
          </Descriptions>

          {/*
            Issue #677 — structured failure diagnostics (per-page retry
            audit, global regen counter, last skill name). Rendered as a
            collapsible pretty-printed JSON block. Only shown when there
            is actual diagnostic payload to display so healthy orders
            don't carry empty noise.
          */}
          {record.failureDetails && (
            <Collapse
              style={{ marginTop: 16 }}
              items={[
                {
                  key: 'failure-details',
                  label: 'Failure Details (issue #677 diagnostics)',
                  children: (
                    <pre
                      style={{
                        maxHeight: 400,
                        overflow: 'auto',
                        fontSize: 12,
                        background: '#f5f5f5',
                        padding: 12,
                        borderRadius: 6,
                        margin: 0,
                      }}
                    >
                      {JSON.stringify(record.failureDetails, null, 2)}
                    </pre>
                  ),
                },
              ]}
            />
          )}

          {/* Lulu / shipping */}
          <Title level={5} style={{ marginTop: 24 }}>
            Print / Shipping
          </Title>
          <Descriptions bordered column={{ xs: 1, sm: 2, lg: 4 }} size="small">
            <Descriptions.Item label="Lulu Job ID">
              {record.luluJobId ?? '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Lulu Status">
              {record.luluStatus ?? '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Tracking URL" span={2}>
              {record.luluTrackingUrl ? (
                <a
                  href={record.luluTrackingUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {record.luluTrackingUrl}
                </a>
              ) : (
                '-'
              )}
            </Descriptions.Item>
          </Descriptions>

          {/* JSONB data sections */}
          <Title level={5} style={{ marginTop: 24 }}>
            Data
          </Title>
          <JsonSection title="Survey Data" data={record.surveyJson} />
          <JsonSection title="Billing Address" data={record.billingAddress} />
          <JsonSection title="Shipping Address" data={record.shippingAddress} />
          {record.environmentConfigJson && (
            <JsonSection
              title="Environment Config"
              data={record.environmentConfigJson}
            />
          )}

          {/* Bulk-edit remaining regeneration attempts */}
          <EditRegenerationCountsForm
            orderId={record.id}
            coverRegenerationCount={record.coverRegenerationCount}
            bookRegenerationCount={record.bookRegenerationCount}
            pageRegenerationCount={record.pageRegenerationCount}
            onSuccess={() => {
              void query.refetch();
            }}
          />

          <AdminRegeneratePageModal
            open={pageRegenModalOpen}
            onClose={() => setPageRegenModalOpen(false)}
            orderId={record.id}
            onSuccess={() => {
              void query.refetch();
            }}
          />
        </>
      )}
    </Show>
  );
}
